import { action, observable, runInAction, computed, makeObservable, reaction } from 'mobx';
import { WorkflowInstanceStore, ProjectsStore, TabsStore, UserProfileStore } from '../../common/stores';
import { ApplicationSession, SessionFilter, SessionStates } from '../../common/services/types';
import { ApplicationSessionChanges } from '../types';
import { SessionService } from '../../common/services';
import PushClient from '../../common/services/ManagePushClient';

const archiveFilterStorageVersion = 1;
const archiveFilterStorageKeyPrefix = `archive-filter-v${archiveFilterStorageVersion}`;
export const archiveSortableFields = ['packageName', 'applicationName', 'projectName', 'created', 'updated'] as const;
export type ArchiveSortField = (typeof archiveSortableFields)[number];

export const isArchiveSortField = (value: unknown): value is ArchiveSortField =>
    typeof value === 'string' && archiveSortableFields.includes(value as ArchiveSortField);

type SessionParams = {
    filter: SessionFilter;
    total: number;
};

type ArchiveFilterStorageItem = Partial<Omit<SessionFilter, 'page' | 'pageSize' | 'sessionStates'>>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const isArchiveSortDirection = (value: unknown): value is SessionFilter['orderByDirection'] =>
    value === 'ASC' || value === 'DESC';

export default class SessionsVisualStore {
    static openSessionsStates: SessionStates[] = ['Pending', 'WaitingAction', 'NotStarted'];

    static archiveSessionsStates: SessionStates[] = ['Aborted', 'Finished', 'Archived'];

    static recentFinishedSessionsStates: SessionStates[] = ['Aborted', 'Finished'];

    openSessions: ApplicationSession[] = [];

    recentFinishedSessions: ApplicationSession[] = [];

    archiveSessions: ApplicationSession[] = [];

    archiveUserFilterIds: string[] = [];

    archiveUserFilterOptionsLoading: boolean = false;

    archiveUserFilterOptionsError: boolean = false;

    openSessionsParams: SessionParams = {
        filter: {
            page: 0,
            pageSize: 24,
            sessionStates: SessionsVisualStore.openSessionsStates
        },
        total: 0
    };

    archiveSessionsParams: SessionParams = {
        filter: {
            page: 0,
            pageSize: 15,
            sessionStates: SessionsVisualStore.archiveSessionsStates
        },
        total: 0
    };

    recentFinishedSessionsParams: SessionParams = {
        filter: {
            page: 0,
            pageSize: 5,
            orderByField: 'updated',
            orderByDirection: 'DESC',
            sessionStates: SessionsVisualStore.recentFinishedSessionsStates
        },
        total: 0
    };

    openSessionsLoading: boolean = false;

    archiveSessionsLoading: boolean = false;

    loadForCurrentUserOnly: boolean = false;

    private restoredArchiveFiltersForUserId?: string;

    private archiveSessionsRequestId = 0;
    private sessionStateRequestId = 0;
    private sessionStateRequests = new Map<string, number>();

    get allUsers() {
        return this.projectsStore.allUsers;
    }

    get allUsersList() {
        const users = this.archiveUserFilterIds.map(id => ({ id, name: this.allUsers[id] || id }));

        return users.sort((a, b) => a.name.localeCompare(b.name));
    }

    get projectIds() {
        return this.projectsStore.projects.map(p => p.id);
    }

    get userId() {
        return this.userProfileStore.currentUserProfileInfo.userId;
    }

    get hasAccessToAllEntities() {
        return this.userProfileStore.hasAccessToAllEntities;
    }

    get archiveFiltersApplied() {
        const filter = this.archiveSessionsParams.filter;
        const userFilterApplied = !this.loadForCurrentUserOnly && this.hasAccessToAllEntities && !!filter.userId;

        return !!(userFilterApplied || filter.dateFrom || filter.dateTo || filter.searchTerm);
    }

    constructor(
        private workflowInstanceStore: WorkflowInstanceStore,
        private projectsStore: ProjectsStore,
        private tabsStore: TabsStore,
        private userProfileStore: UserProfileStore,
        private sessionService: SessionService
    ) {
        makeObservable(this, {
            loadForCurrentUserOnly: observable,
            openSessions: observable,
            recentFinishedSessions: observable,
            archiveSessions: observable,
            archiveUserFilterIds: observable,
            archiveUserFilterOptionsLoading: observable,
            archiveUserFilterOptionsError: observable,
            openSessionsParams: observable,
            archiveSessionsParams: observable,
            openSessionsLoading: observable,
            archiveSessionsLoading: observable,
            allUsers: computed,
            allUsersList: computed,
            projectIds: computed,
            userId: computed,
            archiveFiltersApplied: computed,
            loadApplicationSessions: action.bound,
            updateSessionState: action.bound,
            moveSessionToArchive: action,
            switchToSession: action,
            setOpenSessionsParams: action,
            setArchiveSessionsParams: action,
            clearArchiveFilters: action.bound,
            setOpenSessionsLoading: action,
            setArchiveSessionsLoading: action,
            setLoadForCurrentUserOnly: action,
            loadArchiveUserFilterOptions: action.bound
        });

        this.restoreArchiveFilters();
        reaction(
            () => this.getCurrentUserId(),
            () => {
                if (this.restoreArchiveFilters()) {
                    this.loadArchiveSessions();
                }
            }
        );

        this.projectsStore.sessionChanges.subscribe(s => {
            this.updateSessionState(s);
        });
        PushClient.ConnectionStatusSubject.subscribe(connected => {
            if (connected && PushClient.ReconnectAttemptCount > 0) {
                const promises = [
                    this.loadOpenSessions(),
                    this.loadRecentFinishedSessions(),
                    this.loadArchiveSessions()
                ];
                Promise.all(promises);
            }
        });
    }

    setOpenSessionsParams(openSessionsParams: SessionParams) {
        this.openSessionsParams = openSessionsParams;
    }

    setArchiveSessionsParams(archiveSessionsParams: SessionParams) {
        this.archiveSessionsParams = archiveSessionsParams;
        this.saveArchiveFilters();
    }

    async clearArchiveFilters() {
        const filter = this.archiveSessionsParams.filter;

        this.setArchiveSessionsParams({
            ...this.archiveSessionsParams,
            filter: {
                page: 0,
                pageSize: filter.pageSize,
                sessionStates: SessionsVisualStore.archiveSessionsStates,
                orderByField: filter.orderByField,
                orderByDirection: filter.orderByDirection
            }
        });

        await this.loadArchiveSessions();
    }

    setOpenSessionsLoading(openSessionsLoading: boolean) {
        this.openSessionsLoading = openSessionsLoading;
    }

    setArchiveSessionsLoading(archiveSessionsLoading: boolean) {
        this.archiveSessionsLoading = archiveSessionsLoading;
    }

    setLoadForCurrentUserOnly(loadForCurrentUserOnly: boolean) {
        this.loadForCurrentUserOnly = loadForCurrentUserOnly;
        this.restoreArchiveFilters();
    }

    async loadMoreOpenSessions() {
        try {
            this.setOpenSessionsLoading(true);

            this.setOpenSessionsParams({
                ...this.openSessionsParams,
                filter: {
                    ...this.openSessionsParams.filter,
                    page: this.openSessionsParams.filter.page + 1
                }
            });

            const result = await this.loadApplicationSessions(this.openSessionsParams.filter);

            runInAction(() => {
                const filteredSessions = result.sessions.filter(s => !this.openSessions.some(os => os.id === s.id));
                this.openSessions = [...this.openSessions, ...filteredSessions];
            });

            this.setOpenSessionsParams({ ...this.openSessionsParams, total: result.total });
        } finally {
            this.setOpenSessionsLoading(false);
        }
    }

    async loadOpenSessions() {
        const result = await this.loadApplicationSessions(this.openSessionsParams.filter);

        runInAction(() => {
            this.openSessions = result.sessions;
        });

        this.setOpenSessionsParams({ ...this.openSessionsParams, total: result.total });
    }

    async loadRecentFinishedSessions() {
        const result = await this.loadApplicationSessions(this.recentFinishedSessionsParams.filter);

        runInAction(() => {
            this.recentFinishedSessions = result.sessions;
        });
    }

    async loadArchiveSessions() {
        const requestId = this.archiveSessionsRequestId + 1;
        this.archiveSessionsRequestId = requestId;

        try {
            this.setArchiveSessionsLoading(true);

            const result = await this.loadApplicationSessions(this.archiveSessionsParams.filter);

            if (requestId !== this.archiveSessionsRequestId) {
                return;
            }

            runInAction(() => {
                this.archiveSessions = result.sessions;
            });

            this.setArchiveSessionsParams({ ...this.archiveSessionsParams, total: result.total });
        } finally {
            if (requestId === this.archiveSessionsRequestId) {
                this.setArchiveSessionsLoading(false);
            }
        }
    }

    async loadApplicationSessions(filter: SessionFilter) {
        await this.projectsStore.loadProjectsPromise;

        if (!this.projectIds) {
            return { sessions: [], total: 0 };
        }

        const result = await this.workflowInstanceStore.loadApplicationSessions({
            ...filter,
            projectIds: this.projectIds,
            userId: this.loadForCurrentUserOnly ? this.userId : filter.userId
        });

        await this.projectsStore.loadUserDisplayNames(this.getSessionUserIds(result.sessions));
        return result;
    }

    async updateSessionState(updateModel: ApplicationSessionChanges | null) {
        if (!updateModel) {
            return;
        }
        // Legacy notifications can also remain queued after versioned admission.
        // Resolve every notification against the current persisted state.
        const request = ++this.sessionStateRequestId;
        const id = updateModel.id;
        this.sessionStateRequests.set(id, request);
        const current = await this.getSessionById(id);
        if (this.sessionStateRequests.get(id) !== request) {
            return;
        }
        this.sessionStateRequests.delete(id);
        if (!current) {
            return;
        }
        updateModel = {
            ...updateModel,
            state: current.state,
            error: current.error,
            runtimeSessionId: current.runtimeSessionId
        };

        const sessionId = updateModel.id;
        const sessionToUpdate = this.openSessions.find(a => a.id === sessionId);
        const isOpenSession = SessionsVisualStore.openSessionsStates.includes(updateModel.state);

        if (sessionToUpdate && !isOpenSession) {
            this.removeOpenSession(updateModel.id);
            return;
        }

        if (sessionToUpdate && isOpenSession) {
            this.updateOpenSession(sessionToUpdate, updateModel);
            return;
        }

        if (!sessionToUpdate && isOpenSession) {
            this.addOpenSession(current);
            return;
        }
    }

    addOpenSession(session: ApplicationSession) {
        if (
            !session ||
            !SessionsVisualStore.openSessionsStates.includes(session.state) ||
            this.openSessions.some(s => s.id === session.id)
        ) {
            return;
        }

        runInAction(() => {
            this.openSessions = [session, ...this.openSessions];
            this.setOpenSessionsParams({ ...this.openSessionsParams, total: this.openSessionsParams.total + 1 });
        });
    }

    updateOpenSession(sessionToUpdate: ApplicationSession, updateModel: ApplicationSessionChanges) {
        runInAction(() => {
            const index = this.openSessions.indexOf(sessionToUpdate);
            let newList = this.openSessions.slice();
            newList[index].state = updateModel.state;
            newList[index].error = updateModel.error || '';
            newList[index].runtimeSessionId = updateModel.runtimeSessionId;
            this.openSessions = newList;
        });
    }

    removeOpenSession(sessionId: string) {
        runInAction(() => {
            this.openSessions = this.openSessions.filter(s => s.id !== sessionId);
        });

        this.loadRecentFinishedSessions();
    }

    async getSessionById(sessionId: string) {
        const session = await this.sessionService.getSessionById(sessionId, this.projectIds);
        await this.projectsStore.loadUserDisplayNames(this.getSessionUserIds(session ? [session] : []));
        return session;
    }

    async loadArchiveUserFilterOptions() {
        if (this.archiveUserFilterOptionsLoading || this.loadForCurrentUserOnly || !this.hasAccessToAllEntities) {
            return;
        }

        runInAction(() => {
            this.archiveUserFilterOptionsLoading = true;
            this.archiveUserFilterOptionsError = false;
            this.archiveUserFilterIds = [];
        });

        try {
            await this.projectsStore.loadProjectsPromise;
            const userIds = this.projectIds.length
                ? await this.sessionService.getArchiveUserIdsForProjects(this.projectIds)
                : [];
            await this.projectsStore.loadUserDisplayNames(userIds);
            runInAction(() => {
                this.archiveUserFilterIds = userIds;
            });
        } catch {
            runInAction(() => {
                this.archiveUserFilterOptionsError = true;
            });
        } finally {
            runInAction(() => {
                this.archiveUserFilterOptionsLoading = false;
            });
        }
    }

    startSessionFromExisting(session: ApplicationSession) {
        if (session.packageId) {
            this.workflowInstanceStore.startSession(
                session.packageId,
                session.applicationDefinitionId,
                session.packageName,
                session.applicationExtension
            );
        } else if (session.packageSetId) {
            this.workflowInstanceStore.startSessionForPackageSet(
                session.packageSetId,
                session.applicationDefinitionId,
                session.applicationExtension,
                session.packageName,
                undefined,
                session.isBulkSession
            );
        }
    }

    moveSessionToArchive(sessionId: string) {
        runInAction(() => {
            this.openSessions = this.openSessions.filter(s => s.id !== sessionId);
        });
    }

    async switchToSession(sessionObj: unknown) {
        const session = sessionObj as ApplicationSession;
        await this.tabsStore.addTab({
            id: session.id,
            title: session.packageName,
            isSelected: true,
            type: session.isBulkSession ? 'multidocument' : 'document',
            metadata: {
                packageId: session.packageId,
                sessionId: session.id,
                packageName: session.packageName,
                dynamicUI: undefined,
                sessionError: session.error,
                sessionState: session.state,
                runtimeSessionId: session.runtimeSessionId,
                packageSetId: session.packageSetId,
                projectName: session.projectName,
                projectId: session.projectId,
                isReloadable: session.isReloadable,
                customUI: session.capabilities?.customUI,
                isBulk: session.isBulkSession,
                applicationData: {
                    appName: session.applicationName,
                    appId: session.applicationDefinitionId,
                    appExtension: session.applicationExtension,
                    appSettings: session.applicationSettings,
                    iotaApplication: session.iotaApplication
                }
            }
        });
    }

    private getSessionUserIds(sessions: ApplicationSession[]) {
        return sessions.flatMap(session => [session.userId, session.updatedBy]);
    }

    private restoreArchiveFilters() {
        const userId = this.getCurrentUserId();
        if (!userId || this.restoredArchiveFiltersForUserId === userId) {
            return false;
        }

        this.restoredArchiveFiltersForUserId = userId;
        const storedFilters = this.getStoredArchiveFilters();
        if (!storedFilters) {
            return false;
        }

        const orderByDirection = storedFilters.orderByField ? storedFilters.orderByDirection : undefined;

        this.archiveSessionsParams = {
            ...this.archiveSessionsParams,
            filter: {
                ...this.archiveSessionsParams.filter,
                ...storedFilters,
                orderByDirection,
                sessionStates: SessionsVisualStore.archiveSessionsStates
            }
        };

        return true;
    }

    private getStoredArchiveFilters(): ArchiveFilterStorageItem | null {
        try {
            if (typeof sessionStorage === 'undefined') {
                return null;
            }

            const storageKey = this.getArchiveFilterStorageKey();
            if (!storageKey) {
                return null;
            }

            const storedFilters = sessionStorage.getItem(storageKey);
            if (!storedFilters) {
                return null;
            }

            const parsedValue: unknown = JSON.parse(storedFilters);
            if (!isRecord(parsedValue) || !isRecord(parsedValue.value)) {
                return null;
            }

            return this.mapStoredArchiveFilters(parsedValue.value);
        } catch (error) {
            console.error('Error reading archive filters:', error);
            return null;
        }
    }

    private mapStoredArchiveFilters(value: Record<string, unknown>): ArchiveFilterStorageItem | null {
        const storageItem: ArchiveFilterStorageItem = {};

        if (typeof value.userId === 'string') {
            storageItem.userId = value.userId;
        }

        if (typeof value.dateFrom === 'string') {
            storageItem.dateFrom = value.dateFrom;
        }

        if (typeof value.dateTo === 'string') {
            storageItem.dateTo = value.dateTo;
        }

        if (typeof value.searchTerm === 'string') {
            storageItem.searchTerm = value.searchTerm;
        }

        if (isArchiveSortField(value.orderByField)) {
            storageItem.orderByField = value.orderByField;
        }

        if (isArchiveSortDirection(value.orderByDirection)) {
            storageItem.orderByDirection = value.orderByDirection;
        }

        return Object.keys(storageItem).length ? storageItem : null;
    }

    private saveArchiveFilters() {
        try {
            if (typeof sessionStorage === 'undefined') {
                return;
            }

            if (!this.archiveFiltersRestoredForCurrentUser()) {
                return;
            }

            const storageKey = this.getArchiveFilterStorageKey();
            if (!storageKey) {
                return;
            }

            const storageItem = this.getArchiveFilterStorageItem();

            sessionStorage.setItem(storageKey, JSON.stringify({ value: storageItem }));
        } catch (error) {
            console.error('Error storing archive filters:', error);
        }
    }

    private getArchiveFilterStorageItem(): ArchiveFilterStorageItem {
        const storageItem: Partial<SessionFilter> = { ...this.archiveSessionsParams.filter };
        delete storageItem.page;
        delete storageItem.pageSize;
        delete storageItem.sessionStates;

        return storageItem;
    }

    private getArchiveFilterStorageKey() {
        const userId = this.getCurrentUserId();

        return userId ? `${archiveFilterStorageKeyPrefix}-${userId}` : undefined;
    }

    private archiveFiltersRestoredForCurrentUser() {
        const userId = this.getCurrentUserId();

        return !!userId && this.restoredArchiveFiltersForUserId === userId;
    }

    private getCurrentUserId() {
        return this.userProfileStore.currentUserProfileInfo?.userId;
    }
}
